<template>
  <div class="c-sharing-settings-populations-list">
    <div class="c-sharing-settings-populations-list__scroll">
      <ListContainer
        :list="enrichedPopulations"
        :get-key="(item: PopulationWithSharing) => item.population.id"
        :has-divider="() => false"
        class="c-sharing-settings-populations-list__container"
        :show-hover="true"
      >
        <template #header>
          <PopulationTabListHeader
            first-column-label="Population Name"
            :header-fields="headerFields"
            :all-items-selected="allPopulationsSelected"
            :some-items-selected="somePopulationsSelected"
            :can-edit="canEdit"
            header-class="c-sharing-settings-populations-list__grid c-sharing-settings-populations-list__grid--header"
            @select-all="handleHeaderCheckboxChange"
          />
        </template>
        <template #default="{ item }">
          <PopulationTabPopulationsRow
            :item="item"
            :selected-population-ids="selectedPopulationIds"
            :has-changes="hasPopulationChanges(item.population.id)"
            :can-edit="canEdit"
            :is-greenfield-sharing-blocked="isGreenfieldSharingBlocked"
            @visibility-changed="handleVisibilityChange"
            @population-checkbox-changed="handlePopulationCheckboxChange"
            @preset-changed="handlePresetChange"
            @row-clicked="$emit('row-clicked', $event)"
          />
        </template>
      </ListContainer>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import ListContainer from '@/components/ListContainer.vue';

import useSharingHubChangeTracking from '@/composables/dataSharingHub/useSharingHubChangeTracking';
import type { PopulationWithSharing } from '@/composables/dataSharingHub/useSharingSettingsHub';
import useIncrementalRendering from '@/composables/useIncrementalRendering';
import { SharingType } from '@/constants/data_shares';

import PopulationTabListHeader from './PopulationTabListHeader.vue';
import PopulationTabPopulationsRow from './PopulationTabPopulationsRow.vue';

type PopulationSharingData = {
  visibility?: SharingType;
  presetId?: number | null;
};

// Extended type with current display values (including unsaved changes)
type EnrichedPopulation = PopulationWithSharing & {
  currentVisibility?: SharingType;
  currentPresetId?: number | null;
};

const props = withDefaults(
  defineProps<{
    populations?: PopulationWithSharing[];
    canEdit?: boolean;
    isGreenfieldSharingBlocked?: boolean;
  }>(),
  {
    populations: () => [],
    canEdit: true,
    isGreenfieldSharingBlocked: false,
  },
);

const emit = defineEmits<{
  'populations-selected': [populationIds: number[]];
  'visibility-changed': [populationId: number, visibility: SharingType];
  'preset-changed': [populationId: number, presetId: number | undefined];
  'row-clicked': [populationId: number];
  'has-changes-updated': [hasChanges: boolean];
  'current-values-updated': [
    populationId: number,
    values: { visibility?: SharingType; presetId?: number | null },
  ];
}>();
const selectedPopulationIds = ref<number[]>([]);

const changeTracking = useSharingHubChangeTracking<
  number,
  PopulationSharingData
>();

// Incremental rendering: show items in batches to avoid blocking the UI
const { visibleCount } = useIncrementalRendering({
  dataSource: computed(() => props.populations),
  initialBatchSize: 15,
  batchSize: 15,
});

const enrichedPopulations = computed<EnrichedPopulation[]>(() => {
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  changeTracking.changedCount.value;

  const allItems = props.populations.map((item): EnrichedPopulation => {
    const changes = changeTracking.getChanges(item.population.id);

    const currentVisibility =
      changes?.visibility ?? item.sharingRule?.visibility;
    const currentPresetId = changes?.presetId ?? item.preset?.id ?? null;

    return {
      ...item,
      currentVisibility,
      currentPresetId,
    };
  });

  return allItems.slice(0, visibleCount.value);
});

// Initialize tracking when populations change
// Note: We don't cleanup on filter changes because we want to preserve
// unsaved changes even when populations are temporarily filtered out
watch(
  () => props.populations,
  (newPopulations) => {
    newPopulations.forEach((item) => {
      const populationId = item.population.id;

      if (!changeTracking.isTracking(populationId)) {
        changeTracking.initialize(populationId, {
          visibility: item.sharingRule?.visibility,
          presetId: item.preset?.id ?? null,
        });
      }
    });
  },
  { immediate: true },
);

watch(
  () => changeTracking.hasAnyChanges.value,
  (hasChanges) => {
    emit('has-changes-updated', hasChanges);
  },
  { immediate: true },
);
function handleHeaderCheckboxChange(checked: boolean) {
  if (checked) {
    selectedPopulationIds.value = props.populations.map(
      (item) => item.population.id,
    );
  } else {
    selectedPopulationIds.value = [];
  }
  emit('populations-selected', selectedPopulationIds.value);
}

function handlePopulationCheckboxChange(
  populationId: number,
  checked: boolean,
) {
  if (checked) {
    selectedPopulationIds.value = [
      ...selectedPopulationIds.value,
      populationId,
    ];
  } else {
    selectedPopulationIds.value = selectedPopulationIds.value.filter(
      (id) => id !== populationId,
    );
  }
  emit('populations-selected', selectedPopulationIds.value);
}

function handleVisibilityChange(populationId: number, visibility: SharingType) {
  changeTracking.updateCurrent(populationId, { visibility });
  emit('visibility-changed', populationId, visibility);
  emit('current-values-updated', populationId, { visibility });
}

function handlePresetChange(
  populationId: number,
  presetId: number | undefined,
) {
  const presetIdValue = presetId ?? null;
  changeTracking.updateCurrent(populationId, { presetId: presetIdValue });
  emit('preset-changed', populationId, presetId);
  emit('current-values-updated', populationId, { presetId: presetIdValue });
}

function hasPopulationChanges(populationId: number): boolean {
  return changeTracking.hasChanges(populationId);
}

const allPopulationsSelected = computed(() => {
  return selectedPopulationIds.value.length === props.populations.length;
});

const somePopulationsSelected = computed(() => {
  return (
    selectedPopulationIds.value.length > 0 &&
    selectedPopulationIds.value.length < props.populations.length
  );
});

const headerFields = computed(() => {
  return [
    {
      label: 'Sharing Level',
      tooltip:
        "Choose how much of this population's data you want to share with partners.",
    },
    {
      label: 'Field Presets',
      tooltip: 'Choose which data fields to share by default.',
    },
    {
      label: 'Using Default',
      tooltip:
        'Shows how many partners use the default sharing settings for this population.',
    },
  ];
});

function clearSelections() {
  selectedPopulationIds.value = [];
  emit('populations-selected', []);
}

function setSelection(ids: number[]) {
  selectedPopulationIds.value = [...ids];
}

defineExpose({
  hasAnyChanges: changeTracking.hasAnyChanges,
  changedCount: changeTracking.changedCount,
  getAllChangedIds: changeTracking.getAllChangedIds,
  getAllChangedItems: changeTracking.getAllChangedItems,
  commitChanges: changeTracking.commitChanges,
  reset: changeTracking.reset,
  getChanges: changeTracking.getChanges,
  batchUpdate: changeTracking.batchUpdate,
  getTrackingCount: changeTracking.getTrackingCount,
  enrichedPopulations,
  clearSelections,
  setSelection,
});
</script>

<style lang="pcss" scoped>
@reference "../../../css/theme.css";

.c-sharing-settings-populations-list {
  @apply rounded-16 border border-neutral-border shadow-component overflow-hidden;
  max-height: calc(100vh - 176px);
}

.c-sharing-settings-populations-list__scroll {
  @apply overflow-y-auto overflow-x-auto;
  max-height: calc(100vh - 176px);
}

.c-sharing-settings-populations-list__container {
  @apply border-0 shadow-none rounded-none;
  min-width: 922px;

  :deep(.item--first) {
    @apply rounded-tr-none rounded-tl-none;
  }

  :deep(.c-bitts-divider) {
    border-color: color-mix(
      in srgb,
      theme(colors.neutral-border) 50%,
      transparent
    );
  }
}

.c-sharing-settings-populations-list__grid {
  @apply grid items-center p-4 w-full gap-4;
  grid-template-columns:
    minmax(250px, 2.5fr) minmax(240px, 1.5fr) minmax(240px, 1.5fr)
    minmax(120px, 1.5fr) 40px;
}

.c-sharing-settings-populations-list__grid--header {
  @apply border-b border-neutral-border sticky top-0 z-10 bg-white;
}

:deep(.c-population-tab-list-header__first-col) {
  @apply sticky left-0 z-[11] bg-white;
  margin-left: -4px;
  padding-left: 16px;
}
</style>
